/**
 * Server-backed world config. A "world" bundles layout (rocks, grass, food,
 * nests) together with entity toggles, climate, sim knobs, terrain settings,
 * asset overrides, starting camera, and metadata.
 *
 * Stored under `/home/ubuntu/tune-data/worlds/<slug>.json` with an active
 * pointer at `/api/worlds-active`. The game's /api/world endpoint returns
 * whichever world is currently marked active, so main.ts sees it at boot.
 */

export type AntCasteName =
  | 'worker' | 'soldier' | 'queen' | 'nurse' | 'scout' | 'breeder';

export interface NestPlacement {
  team: 0 | 1;
  x: number;
  z: number;
  radius: number;
}

export interface RockPlacement {
  x: number;
  z: number;
  scale: number;
  rotY: number;
  variantIdx: number;
}

export interface GrassPlacement {
  x: number;
  z: number;
  scale: number;
  rotY: number;
}

export interface FoodPilePlacement {
  x: number;
  z: number;
  amount: number;
  value: number;
  kind: 'pile' | 'corpse';
}

/** Per-creature toggle + count. `customGlbUrl` lets a world override the
 *  default /assets/<id>.glb with an uploaded asset. */
export interface EntityBlock {
  enabled: boolean;
  count: number;
  customGlbUrl?: string;
}

/** User-authored plant. Stationary scatter — no sim, just placements. */
export interface CustomPlant {
  id: string;
  label: string;
  glbUrl: string;
  enabled: boolean;
  count: number;
  scaleMin: number;
  scaleMax: number;
  /** Baked-in orientation tweak applied to every clone before terrain
   *  placement — for fixing GLBs that import rotated, flipped, or
   *  Z-up. Radians. */
  rotX: number;
  rotY: number;
  rotZ: number;
  /** Extra Y offset in metres — push the model up/down off the ground
   *  after auto-levelling. */
  offY: number;
  /** If true, rotate clones to match terrain normal (default). If false
   *  keep Y-up regardless of slope — useful for free-standing props. */
  alignTerrain: boolean;
  /** If true, treat a multi-mesh GLB as a *variant pack* — each instance
   *  picks a random mesh from the scene. Set this on asset packs like
   *  "desert stones" or "desert shrubs". Leave false for a single object
   *  made of multiple parts (trees, sculptures) — otherwise the parts
   *  get scattered separately. */
  isPack?: boolean;
}

/** User-authored creature spawned in the world from any uploaded GLB. */
export interface CustomCreature {
  /** Short stable id (slug). Used as a react-key in the UI and for
   *  per-ant logging. Users never type this — generated from name. */
  id: string;
  label: string;
  glbUrl: string;
  enabled: boolean;
  count: number;
  /** Model scale multiplier — the uploaded GLB's native size is rarely the
   *  right one. Default 0.1 matches our ant-scale world. */
  scale: number;
  /** Locomotion speed in metres/second. */
  speed: number;
  /** Behaviour variant:
   *    'peaceful' — wanders, ignores ants
   *    'predator' — bites ants in melee range
   *    'prey'     — runs away from ants (future: scatter on contact) */
  behavior: 'peaceful' | 'predator' | 'prey';
  /** Hit points. Predators can be killed by ant swarms just like spiders. */
  hp: number;
  /** Bite damage-per-second (predator only). */
  bitePerSec: number;
  /** Baked-in orientation tweak (radians) — fixes GLBs that import
   *  rotated wrong (facing up, flipped, etc.). */
  rotX: number;
  rotY: number;
  rotZ: number;
  /** Extra Y offset in metres — lifts the model off the ground after
   *  auto-levelling. */
  offY: number;
}

export interface ColonyBlock {
  enabled: boolean;
  /** Starting above-ground ant population (all castes combined). */
  startingPopulation: number;
  /** Target caste ratios (0..1, normalised at load). */
  casteRatios: Record<Exclude<AntCasteName, 'queen'>, number>;
  /** Starting food stockpile. */
  startingStockpile: number;
  /** Nest XZ and radius in world metres. */
  nestX: number;
  nestZ: number;
  nestRadius: number;
}

export type TerrainPreset = 'flat' | 'rolling' | 'hilly' | 'mountainous' | 'cracked-desert' | 'custom';

export interface TerrainBlock {
  /** Master terrain visibility. When false, the ground mesh is hidden
   *  (ants still walk on the invisible heightmap). Handy for abstract /
   *  void-style worlds. */
  enabled: boolean;
  preset: TerrainPreset;
  seed: number;
  /** Half-extent of the playable world in metres. */
  worldRadius: number;
  /** Procedural knobs — live-edited when preset === 'custom' or used as the
   *  seed values when a preset is applied. */
  amplitude: number;
  frequency: number;
  octaves: number;
  persistence: number;
  /** Optional heightmap PNG URL (relative to /assets/). When set, overrides
   *  the procedural fields for terrain baking. */
  heightmapUrl?: string;
  /** Vertical scale applied to heightmap-sourced terrain. */
  heightmapScale?: number;
  /** Optional surface-texture image URL — the map that's painted across
   *  the terrain mesh. Replaces the default grass/dirt slope-blend. */
  textureUrl?: string;
  /** How many times the surface texture repeats across the world square.
   *  1 = stretched; 8 = tiles tiny. */
  textureTileRepeat?: number;
  hdriUrl?: string;
}

export interface ClimateBlock {
  startDayOfYear: number;      // 1..365 (or yearLengthDays)
  startTimeOfDay: number;      // 0..24 hours
  dayLengthSec: number;        // seconds per in-game day
  yearLengthDays: number;      // in-game days per year
  rainMinGapDays: number;
  rainMaxGapDays: number;
  seasonalGating: boolean;     // winter pauses mower/lizard/grasshoppers
  freezeDeath: boolean;        // cold kills surface ants
}

export interface SimBlock {
  maxAntsPerColony: number;
  antSpeed: number;            // base m/s
  hungerRateMul: number;       // 1 = baseline
  lifespanMul: number;         // 1 = baseline (per-caste lifespans × this)
  playerTeam: 0 | 1;
  playerCaste: AntCasteName;   // defaults to 'worker'
  recallDurationSec: number;
  nestAlarmRadius: number;
  invasionTriggerRadius: number;
  scoutDetectRadius: number;
}

export interface CameraBlock {
  posX: number; posY: number; posZ: number;
  targetX: number; targetY: number; targetZ: number;
  fovDeg: number;
}

export interface MetaBlock {
  name: string;
  description: string;
  tags: string[];
  authoredAt: number;          // epoch ms
  presetBase?: string;         // if created from a preset, which one
}

export interface WorldConfig {
  /** Bumped whenever we make an incompatible schema change. */
  version: number;
  meta: MetaBlock;
  terrain: TerrainBlock;
  climate: ClimateBlock;
  sim: SimBlock;
  camera: CameraBlock;
  /** Static layout snapshot — populated once by the game on first boot, then
   *  replayed identically on every future load so the map stays stable. */
  terrainSeed: number;
  nests: NestPlacement[];
  rocks: RockPlacement[];
  grass: GrassPlacement[];
  foodPiles: FoodPilePlacement[];
  /** Per-team colony config (index = team 0 red, 1 black). */
  colonies: [ColonyBlock, ColonyBlock];
  /** Toggleable surface creatures. */
  spiders: EntityBlock;
  grasshoppers: EntityBlock;
  rolyPolies: EntityBlock;
  lizard: EntityBlock;
  mower: EntityBlock;
  /** Plant density + enable flags. Built-in grass + rocks are toggleable
   *  independently of their counts. */
  grassEnabled: boolean;
  grassCount: number;
  rocksEnabled: boolean;
  rockCount: number;
  /** Extra scatter plants uploaded per-world. */
  customPlants: CustomPlant[];
  /** Surface pebble scatter — small debris the player can pick up. Workers
   *  nearby enter a frenzy and mimic the pickup/drop behaviour for a while.
   *  Visuals come from the lowpoly-rocks pack with their baked textures —
   *  no per-world tint. */
  pebbles: {
    enabled: boolean;
    count: number;
  };
  /** Per-world food generation. Used the first time a world boots (when
   *  `foodPiles` is empty) to scatter piles around each colony. After
   *  generation the placements live in `foodPiles`; tweaking these
   *  values won't move existing piles, you'd need to clear `foodPiles`
   *  to regenerate. Sugar/leaf colours still live in the global
   *  /api/food endpoint. */
  food: {
    pilesPerColony: number;
    amountMin: number;
    amountMax: number;
  };
  /** Optional GLB asset overrides — key = built-in slot id, value = url. */
  assetOverrides: Record<string, string>;
  /** Per-world replacement audio clip URLs for the built-in sound slots.
   *  Empty / undefined = use the default (procedural for one-shots, the
   *  bundled MP3 for ambient + mower). Anything else points at a clip
   *  uploaded to the sound library. */
  soundOverrides: {
    bite?: string;
    death?: string;
    pickup?: string;
    /** Day-time outdoor ambience — also acts as the fallback when any
     *  higher-priority slot below isn't set. */
    ambient?: string;
    /** Night ambience. Plays when the sun is below the horizon. */
    ambientNight?: string;
    /** Plays during heavy wind gusts. */
    ambientWind?: string;
    /** Plays while raining. */
    ambientRain?: string;
    /** Plays when ≥5 ants are mid-fight. */
    ambientFight?: string;
    /** Plays while the player is underground in nest mode. */
    ambientNest?: string;
    mower?: string;
  };
  /** Per-caste hit-point cap for ants in this world. Drives both fresh
   *  spawn HP and the maxHp lookup used by the late-life frailty drain.
   *  Player ant is exempt and stays at 3. */
  casteHp: Record<AntCasteName, number>;
  /**
   * Per-world selection from the global catalog. Key = catalog entry id
   * (creature OR plant — both share a flat namespace here). Value holds
   * the per-world toggle and population — everything else (orientation,
   * behaviour, GLB url, scale) comes from the catalog entry itself.
   */
  /**
   * Per-world entity selections.  Key = catalog entry id.
   * `enabled` + `count` — always present once the entry is added.
   * `scale` / `rotX` / `rotY` / `rotZ` / `offY` — optional per-world
   * transform overrides. When set they win over the global
   * /api/tune value (for system entries) or the catalog entry's own
   * stored transform (for uploads).  This is how the user gets
   * tiny spiders in one world and massive ones in another.
   */
  worldEntries: Record<string, {
    enabled: boolean;
    count: number;
    scale?: number;
    rotX?: number;
    rotY?: number;
    rotZ?: number;
    offY?: number;
  }>;
  /** Legacy: embedded creatures from before the catalog. Kept for
   *  migration — moved into the catalog on first load, then ignored. */
  customCreatures: CustomCreature[];
}

/** Legacy shape left behind by the old /api/world endpoint. We keep the type
 *  exported so main.ts' existing loadWorld code still compiles; new code
 *  should reach for WorldConfig instead. */
export type WorldState = WorldConfig;

export const WORLD_CONFIG_VERSION = 2;

export function defaultWorldConfig(): WorldConfig {
  return {
    version: WORLD_CONFIG_VERSION,
    meta: {
      name: 'Default',
      description: '',
      tags: [],
      authoredAt: Date.now(),
    },
    terrain: {
      enabled: true,
      preset: 'rolling',
      seed: 1337,
      worldRadius: 40,
      amplitude: 0.8,
      frequency: 0.08,
      octaves: 4,
      persistence: 0.5,
    },
    climate: {
      startDayOfYear: 113,    // April 23
      startTimeOfDay: 8,
      dayLengthSec: 600,
      yearLengthDays: 24,
      rainMinGapDays: 6,
      rainMaxGapDays: 8,
      seasonalGating: true,
      freezeDeath: true,
    },
    sim: {
      maxAntsPerColony: 600,
      antSpeed: 0.9,
      hungerRateMul: 1,
      lifespanMul: 1,
      playerTeam: 0,
      playerCaste: 'worker',
      recallDurationSec: 60,
      nestAlarmRadius: 6,
      invasionTriggerRadius: 2,
      scoutDetectRadius: 8,
    },
    camera: {
      posX: 16, posY: 14, posZ: 22,
      targetX: 0, targetY: 0, targetZ: 0,
      fovDeg: 55,
    },
    terrainSeed: 1337,
    nests: [
      { team: 0, x: -22, z: 0, radius: 0.7 },
      { team: 1, x: 22,  z: 0, radius: 0.7 },
    ],
    rocks: [],
    grass: [],
    foodPiles: [],
    colonies: [
      defaultColonyBlock(0),
      defaultColonyBlock(1),
    ],
    spiders:      { enabled: true, count: 2 },
    grasshoppers: { enabled: true, count: 4 },
    rolyPolies:   { enabled: true, count: 5 },
    lizard:       { enabled: true, count: 1 },
    mower:        { enabled: true, count: 1 },
    grassEnabled: true,
    grassCount: 4000,
    rocksEnabled: true,
    rockCount: 200,
    customPlants: [],
    pebbles: { enabled: true, count: 600 },
    food: { pilesPerColony: 6, amountMin: 30, amountMax: 100 },
    soundOverrides: {
      // Defaults sourced from the "no-food" world's uploaded sound pack.
      // New worlds inherit these; users can swap individual slots from the
      // Sounds tab.
      bite:         '/assets/uploads/0ebf5a53d64cb0b1.mp3',
      pickup:       '/assets/uploads/9e5a7311750d3745.mp3',
      death:        '/assets/uploads/31b580190c771083.mp3',
      ambient:      '/assets/uploads/5d1ce956f56456ba.mp3',
      mower:        '/assets/uploads/c9bf326179cfa91d.mp3',
      ambientNest:  '/assets/uploads/44b5f2b98535c109.mp3',
      ambientNight: '/assets/uploads/2c79c0edbf188318.mp3',
      ambientWind:  '/assets/uploads/fb3b7ca670eb78cf.mp3',
      ambientRain:  '/assets/uploads/bb6d04b7a0a2d235.mp3',
      ambientFight: '/assets/uploads/2baf2a3a7a3e027c.mp3',
    },
    casteHp: {
      worker: 1,
      soldier: 1.8,
      queen: 20,
      nurse: 0.9,
      scout: 0.9,
      breeder: 1.2,
    },
    assetOverrides: {},
    worldEntries: {},
    customCreatures: [],
  };
}

function defaultColonyBlock(team: 0 | 1): ColonyBlock {
  return {
    enabled: true,
    startingPopulation: 30,
    casteRatios: {
      worker: 0.55,
      soldier: 0.15,
      nurse: 0.12,
      scout: 0.08,
      breeder: 0.06,
    },
    startingStockpile: 40,
    nestX: team === 0 ? -22 : 22,
    nestZ: 0,
    nestRadius: 0.7,
  };
}

/** Merge a server-stored object (possibly from an older schema) into a fresh
 *  default config. Missing fields get defaults — that's the forward-compat
 *  path when we bump WORLD_CONFIG_VERSION. */
export function migrateWorldConfig(raw: unknown): WorldConfig {
  const d = defaultWorldConfig();
  if (!raw || typeof raw !== 'object') return d;
  const r = raw as Partial<WorldConfig>;
  return {
    ...d,
    ...r,
    meta:     { ...d.meta,     ...(r.meta     ?? {}) },
    terrain:  { ...d.terrain,  ...(r.terrain  ?? {}) },
    climate:  { ...d.climate,  ...(r.climate  ?? {}) },
    sim:      { ...d.sim,      ...(r.sim      ?? {}) },
    camera:   { ...d.camera,   ...(r.camera   ?? {}) },
    colonies: [
      { ...d.colonies[0], ...((r.colonies?.[0]) ?? {}) },
      { ...d.colonies[1], ...((r.colonies?.[1]) ?? {}) },
    ],
    spiders:      { ...d.spiders,      ...(r.spiders      ?? {}) },
    grasshoppers: { ...d.grasshoppers, ...(r.grasshoppers ?? {}) },
    rolyPolies:   { ...d.rolyPolies,   ...(r.rolyPolies   ?? {}) },
    lizard:       { ...d.lizard,       ...(r.lizard       ?? {}) },
    mower:        { ...d.mower,        ...(r.mower        ?? {}) },
    assetOverrides: { ...d.assetOverrides, ...(r.assetOverrides ?? {}) },
    pebbles: { ...d.pebbles, ...(r.pebbles ?? {}) },
    food: { ...d.food, ...(r.food ?? {}) },
    soundOverrides: { ...d.soundOverrides, ...(r.soundOverrides ?? {}) },
    casteHp: { ...d.casteHp, ...(r.casteHp ?? {}) },
    worldEntries: { ...(r.worldEntries ?? {}) },
    customCreatures: r.customCreatures ?? [],
    customPlants: r.customPlants ?? [],
    nests: r.nests ?? d.nests,
    rocks: r.rocks ?? [],
    grass: r.grass ?? [],
    foodPiles: r.foodPiles ?? [],
  };
}

// ---- API helpers ----

const API_ACTIVE = '/api/world';
const API_LIST   = '/api/worlds';
const API_SLUG   = (slug: string) => `/api/worlds/${encodeURIComponent(slug)}`;
const API_ACTIVE_PTR = '/api/worlds-active';

let cache: WorldConfig | null = null;

export async function loadWorld(): Promise<WorldConfig | null> {
  try {
    const res = await fetch(API_ACTIVE, { cache: 'no-cache' });
    if (!res.ok) return null;
    const json = await res.json();
    if (json && typeof json === 'object') {
      cache = migrateWorldConfig(json);
      return cache;
    }
    return null;
  } catch (err) {
    console.warn('loadWorld failed:', err);
    return null;
  }
}

export async function saveWorld(w: WorldConfig): Promise<void> {
  cache = w;
  const res = await fetch(API_ACTIVE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(w),
  });
  if (!res.ok) throw new Error(`POST ${API_ACTIVE} → ${res.status}`);
}

export function getCachedWorld(): WorldConfig | null { return cache; }

export async function listWorlds(): Promise<{ worlds: string[]; active: string | null }> {
  const res = await fetch(API_LIST, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`GET ${API_LIST} → ${res.status}`);
  return res.json();
}

export async function loadNamedWorld(slug: string): Promise<WorldConfig | null> {
  try {
    const res = await fetch(API_SLUG(slug), { cache: 'no-cache' });
    if (!res.ok) return null;
    const json = await res.json();
    return migrateWorldConfig(json);
  } catch { return null; }
}

export async function saveNamedWorld(slug: string, w: WorldConfig): Promise<void> {
  const res = await fetch(API_SLUG(slug), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(w),
  });
  if (!res.ok) throw new Error(`POST ${API_SLUG(slug)} → ${res.status}`);
}

export async function deleteNamedWorld(slug: string): Promise<void> {
  const res = await fetch(API_SLUG(slug), { method: 'DELETE' });
  if (!res.ok) throw new Error(`DELETE ${API_SLUG(slug)} → ${res.status}`);
}

export async function setActiveWorld(slug: string): Promise<void> {
  const res = await fetch(API_ACTIVE_PTR, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: slug }),
  });
  if (!res.ok) throw new Error(`POST ${API_ACTIVE_PTR} → ${res.status}`);
}

export async function uploadAsset(file: File, kind: 'glb' | 'png' | 'mp3' | 'wav' | 'ogg'): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  const res = await fetch('/api/uploads', { method: 'POST', body: form });
  if (!res.ok) throw new Error(`POST /api/uploads → ${res.status}`);
  const body = await res.json();
  if (!body?.url) throw new Error('upload response missing url');
  return body.url as string;
}

export function makeSlug(name: string): string {
  const s = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
  return s || `world-${Date.now().toString(36)}`;
}
